import { MouseEvent, useMemo, useState } from 'react';

import { Box, Popover, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import GavelIcon from '@mui/icons-material/Gavel';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { SettlementDistributionStatusMap } from '../views/PaymentsView/SettlementsView/constants';

type DistributionType = 'claimant' | 'counselFees' | 'vendor';

interface DistributionParty {
  id: string;
  name: string;
  payeeType: string;
  status: string;
  priority: number;
  stage: string;
  hasDistributionLeg: boolean;
  amount: string;
  paymentMethod: string;
  isExternal: boolean;
  entityRef?: {
    kind: string;
    sequenceId: number;
    deductionId?: number;
    netSettlementId?: number;
  };
}

interface DistributionGroup {
  payeeType: string;
  status: string;
  priority: number;
  parties: DistributionParty[];
}

interface SettlementSequence {
  sequenceNumber: number;
  status?: string;
  groups?: DistributionGroup[];
  paidAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  attorneyFeesPaidAmount?: number | null;
  deductionsPaidAmount?: number | null;
  netSettlementPaidAmount?: number | null;
}

interface SettlementsDistributionCellProps {
  settlementId: number;
  sequences?: SettlementSequence[];
}

interface DistributionItem {
  name: string;
  status: string;
  description: string;
  amount: string;
  paymentMethod: string;
  isExternal?: boolean;
}

interface DistributionPopoverData {
  title: string;
  count: number;
  items: DistributionItem[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  waveContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  waveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  waveLabel: {
    fontFamily: 'Inter',
    fontSize: '10px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.75)',
    minWidth: '20px',
    paddingTop: '2px',
  },
  icon: {
    fontSize: 18,
    cursor: 'pointer',
    borderRadius: '50%',
    padding: '2px',
    transition: 'all 0.2s ease',

    '&:hover': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  disabledIcon: {
    cursor: 'not-allowed',
    opacity: 0.4,

    '&:hover': {
      transform: 'none',
      backgroundColor: 'transparent',
    },
  },
  paper: {
    pointerEvents: 'auto',
    padding: '16px',
    background: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    width: '280px',
  },
  sectionTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  sectionIcon: {
    fontSize: 20,
  },
  sectionTitle: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  itemName: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.common.white,
  },
  itemStatus: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
  },
  itemDescription: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.secondary.main,
    marginTop: '4px',
  },
  emptyText: {
    fontFamily: 'Inter',
    fontSize: '13px',
    color: theme.palette.secondary.main,
  },
  divider: {
    height: '1px',
    background: theme.palette.primary.main,
    margin: '8px 0',
  },
}));

const formatPayeeType = (payeeType?: string) => {
  if (!payeeType) {
    return '-';
  }

  return payeeType
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (value) => value.toUpperCase())
    .trim();
};

const getGroupsByDistributionType = (
  groups: DistributionGroup[] = [],
  type: DistributionType
) => {
  if (type === 'claimant') {
    return groups.filter((group) => group.payeeType === 'Customer');
  }

  if (type === 'counselFees') {
    return groups.filter((group) => group.payeeType === 'AttyFee');
  }

  return groups.filter(
    (group) => group.payeeType === 'VendorDeduction' || group.payeeType === 'Expense'
  );
};

const getHighestPriorityGroup = (groups: DistributionGroup[] = []) => {
  if (!groups.length) {
    return undefined;
  }

  return [...groups].sort((a, b) => a.priority - b.priority)[0];
};

const getDistributionIconColor = (
  groups: DistributionGroup[] = [],
  type: DistributionType
) => {
  const matchedGroups = getGroupsByDistributionType(groups, type);
  const highestPriorityGroup = getHighestPriorityGroup(matchedGroups);

  const status =
    highestPriorityGroup?.status as keyof typeof SettlementDistributionStatusMap;

  return SettlementDistributionStatusMap[status]?.color || 'rgba(255, 255, 255, 0.35)';
};

const getDistributionStatusColor = (status?: string) => {
  const statusKey = status as keyof typeof SettlementDistributionStatusMap;

  return SettlementDistributionStatusMap[statusKey]?.color || 'rgba(255, 255, 255, 0.75)';
};

const buildDistributionData = (
  sequence: SettlementSequence | undefined,
  type: DistributionType | undefined
): DistributionPopoverData | null => {
  if (!sequence || !type) {
    return null;
  }

  const matchedGroups = getGroupsByDistributionType(sequence.groups || [], type);

  const items: DistributionItem[] = matchedGroups.flatMap((group) =>
    group.parties.map((party) => ({
      name: party.name || '-',
      status: party.status || group.status || sequence.status || '-',
      description: `${formatPayeeType(party.payeeType)}`,
      amount: party.amount || '-',
      paymentMethod: party.paymentMethod || '-',
      isExternal: party.isExternal,
    }))
  );

  if (type === 'claimant') {
    return {
      title: 'Claimant',
      count: items.length,
      items,
    };
  }

  if (type === 'counselFees') {
    return {
      title: 'Counsel fees',
      count: items.length,
      items,
    };
  }

  return {
    title: 'Vendor',
    count: items.length,
    items,
  };
};

const SettlementsDistributionCell = ({
  settlementId,
  sequences = [],
}: SettlementsDistributionCellProps) => {
  const { classes, cx } = useStyles();

  const displaySequences = sequences;

  const [distributionPopover, setDistributionPopover] = useState<{
    anchorEl: Element | null;
    sequenceNumber?: number;
    type?: DistributionType;
  } | null>(null);

  const openDistributionPopover = Boolean(distributionPopover?.anchorEl);

  const selectedSequence = useMemo(() => {
    if (!distributionPopover?.sequenceNumber) {
      return undefined;
    }

    return displaySequences.find(
      (seq) => String(seq.sequenceNumber) === String(distributionPopover.sequenceNumber)
    );
  }, [displaySequences, distributionPopover?.sequenceNumber]);

  const selectedDistributionGroups = useMemo(() => {
    if (!selectedSequence || !distributionPopover?.type) {
      return [];
    }

    return getGroupsByDistributionType(selectedSequence.groups || [], distributionPopover.type);
  }, [selectedSequence, distributionPopover?.type]);

  const selectedDistributionData = useMemo(() => {
    return buildDistributionData(selectedSequence, distributionPopover?.type);
  }, [selectedSequence, distributionPopover?.type]);

  const handleDistributionIconClick = (
    event: MouseEvent<SVGSVGElement>,
    sequenceNumber: number,
    type: DistributionType
  ) => {
    event.stopPropagation();

    setDistributionPopover({
      anchorEl: event.currentTarget,
      sequenceNumber,
      type,
    });
  };

  const handleDistributionPopoverClose = () => {
    setDistributionPopover(null);
  };

  const getPopoverIconColor = () => {
    const highestPriorityGroup = getHighestPriorityGroup(selectedDistributionGroups);

    const status =
      highestPriorityGroup?.status as keyof typeof SettlementDistributionStatusMap;

    return SettlementDistributionStatusMap[status]?.color || 'rgba(255, 255, 255, 0.35)';
  };

  const renderPopoverHeaderIcon = () => {
    const iconSx = {
      color: getPopoverIconColor(),
    };

    if (distributionPopover?.type === 'claimant') {
      return <PermIdentityIcon className={classes.sectionIcon} sx={iconSx} />;
    }

    if (distributionPopover?.type === 'counselFees') {
      return <GavelIcon className={classes.sectionIcon} sx={iconSx} />;
    }

    if (distributionPopover?.type === 'vendor') {
      return <StorefrontIcon className={classes.sectionIcon} sx={iconSx} />;
    }

    return null;
  };

  return (
    <>
      <Box className={classes.waveContainer}>
        {displaySequences.map((seq, seqIdx) => {
          const claimantData = buildDistributionData(seq, 'claimant');
          const counselFeesData = buildDistributionData(seq, 'counselFees');
          const vendorData = buildDistributionData(seq, 'vendor');

          const isClaimantDisabled = !claimantData?.items.length;
          const isCounselFeesDisabled = !counselFeesData?.items.length;
          const isVendorDisabled = !vendorData?.items.length;

          return (
            <Box
              key={`${settlementId}-${seq.sequenceNumber}-${seqIdx}`}
              className={classes.waveRow}
            >
              <Typography className={classes.waveLabel}>W{seq.sequenceNumber}</Typography>

              <PermIdentityIcon
                className={cx(classes.icon, isClaimantDisabled && classes.disabledIcon)}
                sx={{
                  color: getDistributionIconColor(seq.groups, 'claimant'),
                }}
                onClick={
                  isClaimantDisabled
                    ? undefined
                    : (event) =>
                        handleDistributionIconClick(event, Number(seq.sequenceNumber), 'claimant')
                }
              />

              <GavelIcon
                className={cx(classes.icon, isCounselFeesDisabled && classes.disabledIcon)}
                sx={{
                  color: getDistributionIconColor(seq.groups, 'counselFees'),
                }}
                onClick={
                  isCounselFeesDisabled
                    ? undefined
                    : (event) =>
                        handleDistributionIconClick(
                          event,
                          Number(seq.sequenceNumber),
                          'counselFees'
                        )
                }
              />

              <StorefrontIcon
                className={cx(classes.icon, isVendorDisabled && classes.disabledIcon)}
                sx={{
                  color: getDistributionIconColor(seq.groups, 'vendor'),
                }}
                onClick={
                  isVendorDisabled
                    ? undefined
                    : (event) =>
                        handleDistributionIconClick(event, Number(seq.sequenceNumber), 'vendor')
                }
              />
            </Box>
          );
        })}
      </Box>

      <Popover
        open={openDistributionPopover}
        anchorEl={distributionPopover?.anchorEl}
        onClose={handleDistributionPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          className: classes.paper,
        }}
      >
        <Box>
          {selectedDistributionData ? (
            <>
              <Box className={classes.sectionTitleWrapper}>
                {renderPopoverHeaderIcon()}

                <Typography className={classes.sectionTitle}>
                  {selectedDistributionData.title} ({selectedDistributionData.count})
                </Typography>
              </Box>

              {selectedDistributionData.items.length ? (
                selectedDistributionData.items.map((item, index) => {
                  const showDivider = index !== selectedDistributionData.items.length - 1;

                  return (
                    <Box key={`${item.name}-${index}`}>
                      <Box className={classes.itemHeader}>
                        <Typography className={classes.itemName}>{item.name}</Typography>

                        <Typography
                          className={classes.itemStatus}
                          sx={{
                            color: getDistributionStatusColor(item.status),
                          }}
                        >
                          {item.status}
                        </Typography>
                      </Box>

                      <Typography className={classes.itemDescription}>
                        {item.description} · {item.amount} · {item.paymentMethod} {item.isExternal && '(External)'}
                      </Typography>

                      {showDivider && <Box className={classes.divider} />}
                    </Box>
                  );
                })
              ) : (
                <Typography className={classes.emptyText}>
                  No distribution data available.
                </Typography>
              )}
            </>
          ) : (
            <Typography className={classes.emptyText}>No distribution data available.</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SettlementsDistributionCell;